import React, { useContext, useEffect, useState } from "react";
import { Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useMutation } from "react-query";

import GlobalContext from "src/context/global-context";
import {
  APISomethingWrongMsg,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import { getErrorMessage } from "src/lib/utils";
import usePermissions from "src/hooks/use-permissions";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import client from "../../api";

interface KafkaSyncHistoryItem {
  id: number;
  trace_number: string;
  card_id: string;
  user_id: number;
  sync_key: string;
  status: string;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

interface KafkaSyncHistoryResponse {
  status: string;
  code: number;
  message: string;
  data: Record<string, never>;
  items: KafkaSyncHistoryItem[];
  totalcount: number;
  page: number;
  limit: number;
  totalPages: number;
}

const KafkaSyncHistory: React.FC = () => {
  const isMounted = useIsMounted();

  const { canView, canList } = usePermissions(permissionConstants.CARD_KAFKA_SYNC_HISTORY);
  const canViewSyncHistory = canView || canList;

  const [dataSource, setDataSource] = useState<KafkaSyncHistoryItem[]>([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);

  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);

  const { mutate: getSyncKafkaHistoryList, isLoading } = useMutation(
    client.cardManagementOtherApis?.getSyncKafkaHistoryList,
    {
      onSuccess: (res: KafkaSyncHistoryResponse) => {
        setDataSource(res?.items || []);
        setTotal(res?.totalcount || 0);
      },

      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else if (error.response) {
          setAlertText(getErrorMessage(error.response.data));
        } else {
          setAlertText(APISomethingWrongMsg);
        }

        setIsAlertOpened(true);
      },
    },
  );

  useEffect(() => {
    if (!isMounted || !canViewSyncHistory) return;

    getSyncKafkaHistoryList({
      page,
      limit,
    });
  }, [page, limit, isMounted, canViewSyncHistory, getSyncKafkaHistoryList]);

  const columns: ColumnsType<KafkaSyncHistoryItem> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: "Trace Number",
      dataIndex: "trace_number",
      key: "trace_number",
    },
    {
      title: "Card ID",
      dataIndex: "card_id",
      key: "card_id",
    },
    {
      title: "User ID",
      dataIndex: "user_id",
      key: "user_id",
    },
    {
      title: "Sync Key",
      dataIndex: "sync_key",
      key: "sync_key",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag color={status === "COMPLETED" ? "success" : "error"}>{status}</Tag>
      ),
    },
    {
      title: "Error Message",
      dataIndex: "error_message",
      key: "error_message",
      render: (message: string | null) => message || "-",
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (date: string) => (date ? new Date(date).toLocaleString() : "-"),
    },
    {
      title: "Updated At",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (date: string) => (date ? new Date(date).toLocaleString() : "-"),
    },
  ];

  return (
    <div className="p-6 bg-white rounded-lg">
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-xl font-semibold">Sync History</h2>
      </div>

      {!canViewSyncHistory ? (
        <div className="flex flex-col items-center justify-center bg-white rounded-[12px] p-8 mt-2 sm:mt-4 text-center shadow-sm">
          <p className="font-manrope text-[16px] font-semibold text-secondary">
            You do not have permission to view kafka sync history.
          </p>
        </div>
      ) : (
        <Table<KafkaSyncHistoryItem>
          columns={columns}
          dataSource={dataSource}
          loading={isLoading}
          rowKey="id"
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showSizeChanger: false,
            position: ["bottomCenter"],
            onChange: (currentPage) => {
              setPage(currentPage);
            },
          }}
          scroll={{ x: 1200 }}
        />
      )}
    </div>
  );
};

export default KafkaSyncHistory;
